import { useCallback } from 'react';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useAppSelector } from '@/store';
import { MarketplaceListing, MyListing } from '@/types/residents/marketplaceListing';

type NavigationProp = StackNavigationProp<RootStackParamList>;

/**
 * Hook that provides navigation to listing detail screens.
 * Automatically determines whether to show the owner view (MyListingItemDetail)
 * or the buyer view (MarketplaceItemDetail) based on listing ownership.
 */
export const useListingNavigation = () => {
  const navigation = useNavigation<NavigationProp>();
  const user = useAppSelector((state) => state.auth.user);

  /**
   * Check if a listing belongs to the current user
   */
  const isOwnListing = useCallback(
    (listing: MarketplaceListing): boolean => {
      return !!(user?.accountId && String(user.accountId) === listing.seller.id);
    },
    [user?.accountId]
  );

  /**
   * Convert a MarketplaceListing to MyListing with default values
   */
  const toMyListing = useCallback((listing: MarketplaceListing): MyListing => {
    return {
      ...listing,
      status: 'active',
      views: 0,
      saved: 0,
    };
  }, []);

  /**
   * Navigate to the appropriate listing detail screen based on ownership.
   * - Owner's listing → MyListingItemDetail (can edit, delete, mark as sold)
   * - Other's listing → MarketplaceItemDetail (can contact, bookmark, report)
   * 
   * @param listing - The listing to navigate to
   * @param usePush - Use navigation.push instead of navigate (creates new screen instance)
   */
  const navigateToListing = useCallback(
    (listing: MarketplaceListing, usePush = false) => {
      if (isOwnListing(listing)) {
        const myListing = toMyListing(listing);
        if (usePush) {
          navigation.push('MyListingItemDetail', { listing: myListing });
        } else {
          navigation.navigate('MyListingItemDetail', { listing: myListing });
        }
      } else {
        if (usePush) {
          navigation.push('MarketplaceItemDetail', { listing });
        } else {
          navigation.navigate('MarketplaceItemDetail', { listing });
        }
      }
    },
    [navigation, isOwnListing, toMyListing]
  );

  return {
    navigateToListing,
    isOwnListing,
    toMyListing,
  };
};
